import { useOrchestratorState } from 'nudge-renderer-utils';
import { shouldRenderTour, isTourCurrentlyActive } from 'onboarding-tours/util/activeTour';
import { useEffect, useId, useMemo } from 'react';

/**
 * Generate a unique context identifier for the current window/iframe
 * This ensures component IDs are unique across different window contexts
 */
const getContextId = () => {
  // Create a unique identifier for this window context
  const topWindow = window.top || window;
  const currentWindow = window;

  // If we're in the top window, use 'main'
  if (topWindow === currentWindow) {
    return 'main';
  }

  // For iframes, try to get a meaningful identifier
  try {
    // Try to use iframe name or id if available
    const frameElement = currentWindow.frameElement;
    if (frameElement) {
      const frameName = frameElement.name || frameElement.id;
      if (frameName) {
        return `iframe-${frameName}`;
      }
    }
  } catch (e) {
    // Cross-origin restrictions might prevent accessing frameElement
  }

  // Fallback: generate a unique identifier based on window properties
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  return `iframe-${timestamp}-${random}`;
};
export const useCanRenderNudge = ({
  checkActiveTour = false,
  id = '',
  isOpen = true,
  weight = 1
} = {}) => {
  const {
    setActiveComponent,
    canRenderNudge,
    activeComponentId,
    removeActiveComponent
  } = useOrchestratorState();
  const reactId = useId();

  // Create a globally unique ID by combining context and React ID
  const componentId = useMemo(() => id || `${getContextId()}:${reactId}`, [reactId, id]);

  // TODO: remove this when onboarding-tours are deprecated
  // Useful because it will detect tourId in URL and linked tour storage before a tour renders.
  const hasActiveTour = checkActiveTour ? shouldRenderTour() || isTourCurrentlyActive() : false;

  // This is where the fun happens - we can render if:
  // 1. There's no active tour (will render with linked data or URL param)
  // 2. No component is currently active (activeComponentId is null)
  // 2a. OR This component is already the active one
  //
  // The magic here is that if an iframe claims the activeComponentId, other iframes
  // won't be able to claim it until it's released (set to null or changed)
  const allowRender = !hasActiveTour && canRenderNudge && (activeComponentId === null || activeComponentId === componentId);
  useEffect(() => {
    if (isOpen) {
      // Add to wait list
      setActiveComponent(componentId, weight);
    } else {
      // component is closed, release the render spot.
      // Internally check if that's the active component before removing it
      removeActiveComponent(componentId);
    }
  }, [setActiveComponent,
  // intentional: run this useEffect when activeComponentId changes as setActiveComponent depends on it
  activeComponentId, isOpen, componentId, weight, removeActiveComponent]);

  // Cleanup on unmount: remove this component's ID if it's still active
  useEffect(() => {
    return () => {
      removeActiveComponent(componentId);
    };
  }, [componentId, removeActiveComponent]);
  return {
    canRender: allowRender
  };
};